import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import { AppShell, TopNav, NavDrawer, BodyGrid, PageBody, useDrawer } from '../components/Layout';
import { apiFetch } from '../services/api';
import { useWorkspaces } from '../hooks/useWorkspaces';
import { auth } from '../services/firebase';
import { useHelpBanner } from '../components/HelpBanner';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3070';
const LIMIT_OPTIONS = [3, 5, 7, 10];

const KNOWN_RAW_KEYS = new Set([
  'admin_email', 'day_summary', 'morning_message', 'blockers_noted', 'triggered_by',
  'tasks', 'completed_tasks', 'last_task_completed_at',
]);

// ─── Helpers ──────────────────────────────────────────────────────────────────

function timeAgo(ts) {
  if (!ts) return '';
  const s = Math.floor((Date.now() - new Date(ts)) / 1000);
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

function fmtDate(d) {
  if (!d) return '';
  return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

async function getToken() {
  try { return await auth.currentUser?.getIdToken(); } catch { return null; }
}

function toBullets(text) {
  if (!text) return text;
  const lines = text.split('\n');
  const alreadyMd = lines.some(l => /^[\-\*#>]/.test(l.trim()));
  if (alreadyMd) return text;
  if (lines.length === 1 && text.length > 100) {
    return text.split(/(?<=\.)\s+/).filter(s => s.trim()).map(s => `- ${s.trim()}`).join('\n');
  }
  return lines.map(l => l.trim() === '' ? '' : `- ${l.trim()}`).join('\n');
}

function MdText({ children }) {
  if (!children) return null;
  return <div className="md-body"><ReactMarkdown>{children}</ReactMarkdown></div>;
}

const dateInputStyle = {
  padding: '5px 8px', border: '1px solid var(--paper-3)',
  borderRadius: 'var(--r)', background: 'var(--white)',
  fontSize: '11px', color: 'var(--ink)', fontFamily: 'var(--sans)',
};

// ═══════════════════════════════════════════════════════════════════════════════
// LEFT — Run Log Panel
// ═══════════════════════════════════════════════════════════════════════════════

function RunLogPanel({ workspaceId, activeWorkspace }) {
  const [limit, setLimit]                      = useState(3);
  const [dateFrom, setDateFrom]                = useState('');
  const [dateTo, setDateTo]                    = useState('');
  const [runs, setRuns]                        = useState([]);
  const [loading, setLoading]                  = useState(true);
  const [expanded, setExpanded]                = useState({});
  const [nextDayStatus, setNextDayStatus]      = useState(null);
  const [approvingNextDay, setApprovingNextDay] = useState(false);

  const fetchAll = useCallback(async () => {
    if (!workspaceId) return;
    setLoading(true);
    try {
      let qs = `?limit=${limit}`;
      if (dateFrom) qs += `&date_from=${dateFrom}`;
      if (dateTo)   qs += `&date_to=${dateTo}`;
      const [runsData, statusData] = await Promise.all([
        apiFetch(`/api/workspaces/${workspaceId}/ceo-run-log${qs}`),
        apiFetch(`/api/workspaces/${workspaceId}/next-day-status`),
      ]);
      const list = Array.isArray(runsData) ? runsData : [];
      setRuns(list);
      setNextDayStatus(statusData?.next_day_status ?? null);
      if (list.length > 0) {
        setExpanded(prev => {
          const firstId = list[0].id;
          if (prev[firstId]) return prev;
          const raw = list[0].raw_response || {};
          const extraKeys = Object.keys(raw).filter(k => !KNOWN_RAW_KEYS.has(k) && raw[k]);
          const allOpen = { summary: true, morning: true, blockers: true, tasks: true, completed_tasks: true };
          extraKeys.forEach(k => { allOpen[k] = true; });
          return { ...prev, [firstId]: allOpen };
        });
      }
    } catch (e) {
      setRuns([]);
    } finally {
      setLoading(false);
    }
  }, [workspaceId, limit, dateFrom, dateTo]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  async function handleApprove() {
    setApprovingNextDay(true);
    try {
      const data = await apiFetch(`/api/workspaces/${workspaceId}/proceed-to-next-day`, { method: 'POST' });
      setNextDayStatus(data.next_day_status);
    } catch (e) {}
    finally { setApprovingNextDay(false); }
  }

  async function handleUnapprove() {
    setApprovingNextDay(true);
    try {
      const data = await apiFetch(`/api/workspaces/${workspaceId}/lock-next-day`, { method: 'POST' });
      setNextDayStatus(data.next_day_status);
    } catch (e) {}
    finally { setApprovingNextDay(false); }
  }

  function toggleSection(runId, section) {
    setExpanded(prev => ({
      ...prev,
      [runId]: { ...prev[runId], [section]: !prev[runId]?.[section] },
    }));
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      <div style={{ padding: '20px 20px 14px', borderBottom: '1px solid var(--paper-3)', flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
          <div>
            <h2 style={{ fontFamily: 'var(--serif)', fontSize: '18px', fontWeight: '400', margin: 0 }}>
              Detailed Daily Activity Log
            </h2>
            <p style={{ fontSize: '12px', color: 'var(--ink-mute)', margin: '2px 0 0' }}>
              Nightly agent summaries
            </p>
          </div>
          <select
            value={limit}
            onChange={e => setLimit(Number(e.target.value))}
            style={{
              padding: '5px 10px', border: '1px solid var(--paper-3)',
              borderRadius: 'var(--r)', background: 'var(--white)',
              fontSize: '12px', color: 'var(--ink)', cursor: 'pointer', fontFamily: 'var(--sans)',
            }}
          >
            {LIMIT_OPTIONS.map(n => <option key={n} value={n}>Last {n} runs</option>)}
          </select>
        </div>

        {(nextDayStatus === 'approved' || nextDayStatus === null) && (
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            gap: '10px', padding: '10px 12px', marginBottom: '10px',
            background: 'rgba(93,202,165,0.1)', border: '1px solid rgba(93,202,165,0.3)',
            borderRadius: 'var(--r)',
          }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: '#1a7a5a' }}>
              <span>✅</span>
              <span>
                <strong>Thanks for approving the pending tasks. Your AI employees  will work on your tasks tonight.
                 <br/>Send a message to CEO or create a new task if anything comes to your mind.</strong>
                {nextDayStatus === null && ' (No review needed — first run)'}
              </span>
            </span>
            {nextDayStatus === 'approved' && (
              <button
                onClick={handleUnapprove}
                disabled={approvingNextDay}
                style={{
                  padding: '4px 10px', background: 'none',
                  border: '1px solid rgba(93,202,165,0.5)', borderRadius: 'var(--r)',
                  fontFamily: 'var(--sans)', fontSize: '11px', color: '#1a7a5a',
                  cursor: approvingNextDay ? 'default' : 'pointer',
                  flexShrink: 0, opacity: approvingNextDay ? 0.5 : 1,
                }}
              >
                {approvingNextDay ? '…' : '↩ Un-approve'}
              </button>
            )}
          </div>
        )}

        {nextDayStatus === 'pending_review' && (
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            gap: '10px', padding: '10px 12px', marginBottom: '10px',
            background: 'rgba(55,138,221,0.08)', border: '1px solid rgba(55,138,221,0.25)',
            borderRadius: 'var(--r)',
          }}>
            <div>
              <p style={{ fontSize: '12px', fontWeight: '600', color: '#1a5fa0', margin: '0 0 2px' }}>
                Tonight's plan is ready for your review. Review the open tasks, add guidance if you wish.
                After you approve, AI employees will
                start working on their tasks tonight.
              </p>
              <p style={{ fontSize: '11px', color: 'var(--ink-mute)', margin: 0 }}>
                Review the run below, then approve to let CEO proceed tonight.

              </p>
            </div>
            <button
              onClick={handleApprove}
              disabled={approvingNextDay}
              style={{
                padding: '7px 14px', background: '#378add', color: '#fff',
                border: 'none', borderRadius: 'var(--r)', fontFamily: 'var(--sans)',
                fontSize: '12px', fontWeight: '600',
                cursor: approvingNextDay ? 'default' : 'pointer',
                whiteSpace: 'nowrap', opacity: approvingNextDay ? 0.6 : 1, flexShrink: 0,
              }}
            >
              {approvingNextDay ? '…' : '✅ Approve'}
            </button>
          </div>
        )}

        <Link
          to={`/dashboard/ceo_messages?workspace_id=${workspaceId}`}
          style={{
            padding: '4px 10px', background: 'rgba(93, 202, 165, 0.1)',
            border: '1px solid rgba(93,202,165,0.5)', borderRadius: 'var(--r)',
            fontFamily: 'var(--sans)', marginBottom: '12px', fontSize: '11px',
            color: '#1a7a5a', textDecoration: 'none', whiteSpace: 'nowrap',
            flexShrink: 0, display: 'inline-block',
          }}
        >
          ✉️ Send Private Message to CEO
        </Link>

        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap', marginTop: '10px' }}>
          <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} style={dateInputStyle} />
          <span style={{ fontSize: '11px', color: 'var(--ink-mute)' }}>to</span>
          <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} style={dateInputStyle} />
          {(dateFrom || dateTo) && (
            <button
              onClick={() => { setDateFrom(''); setDateTo(''); }}
              style={{ fontSize: '11px', color: 'var(--ink-mute)', background: 'none', border: 'none', cursor: 'pointer' }}
            >
              Clear ✕
            </button>
          )}
        </div>
      </div>

      <div style={{ flex: 1, overflowY: 'auto', padding: '12px 20px 20px' }}>
        {loading
          ? <LoadingRows n={3} />
          : runs.length === 0
            ? <EmptyState icon="📋" text="No runs found for this range." />
            : runs.map((run, i) => (
                <RunCard
                  key={run.id}
                  run={run}
                  index={i}
                  workspaceId={workspaceId}
                  expanded={expanded[run.id] || {}}
                  onToggle={section => toggleSection(run.id, section)}
                />
              ))
        }
        <div>
          <h1 style={{ fontFamily: 'var(--serif)', fontSize: '22px', fontWeight: '400', margin: 0 }}>
            AI CEO Report
          </h1>
          {activeWorkspace?.feed_log && (
            <p style={{
              fontSize: '12px', color: 'black', margin: '4px 0 0',
              maxHeight: '15.5em', overflowY: 'auto', lineHeight: 1.6,
              whiteSpace: 'pre-wrap', paddingRight: '4px',
            }}>
              {activeWorkspace.feed_log}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Run Card ─────────────────────────────────────────────────────────────────

function RunCard({ run, index, expanded, onToggle, workspaceId }) {
  const raw            = run.raw_response || {};
  const extraKeys      = Object.keys(raw).filter(k => !KNOWN_RAW_KEYS.has(k) && raw[k]);
  const tasks          = Array.isArray(raw.tasks) ? raw.tasks : [];
  const completedTasks = Array.isArray(raw.completed_tasks) ? raw.completed_tasks : [];
  const taskListUrl    = `/dashboard/tasks?workspace_id=${workspaceId}`;
  const doneListUrl    = `/dashboard/tasks?workspace_id=${workspaceId}&status=Done`;

  return (
    <div style={{
      border: '1px solid var(--paper-3)', borderRadius: 'var(--r)',
      background: 'var(--white)', marginBottom: '12px', overflow: 'hidden',
      animation: 'fadeSlideIn 0.3s ease both',
      animationDelay: `${index * 60}ms`,
    }}>
      <div style={{
        padding: '12px 14px', borderBottom: '1px solid var(--paper-2)',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        flexWrap: 'wrap', gap: '6px', background: 'var(--paper)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
          <span style={{ fontSize: '13px', fontWeight: '600', color: 'var(--ink)' }}>{fmtDate(run.run_date)}</span>
          <span style={{
            fontSize: '10px', fontWeight: '600', padding: '2px 7px', borderRadius: '100px',
            background: 'rgba(14,14,13,0.08)', color: 'var(--ink)',
            textTransform: 'uppercase', letterSpacing: '0.04em',
          }}>
            Phase {run.phase}
          </span>
          {run.triggered_by && (
            <span style={{
              fontSize: '10px', padding: '2px 7px', borderRadius: '100px',
              background: 'rgba(55,138,221,0.1)', color: '#1a5fa0', fontWeight: '500',
            }}>
              {run.triggered_by.replace(/_/g, ' ')}
            </span>
          )}
          {run.admin_email && (
            <span style={{ fontSize: '10px', color: 'var(--ink-mute)' }}>{run.admin_email}</span>
          )}
        </div>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          {run.tasks_created != null && (
            <span style={{ fontSize: '11px', color: 'var(--ink-mute)' }}>
              {run.tasks_created} task{run.tasks_created !== 1 ? 's' : ''}
            </span>
          )}
          {(run.prompt_tokens || run.completion_tokens) && (
            <span style={{ fontSize: '10px', color: 'var(--ink-mute)', fontFamily: 'monospace' }}>
              ↑{(run.prompt_tokens || 0).toLocaleString()} ↓{(run.completion_tokens || 0).toLocaleString()} tok
            </span>
          )}
        </div>
      </div>

      <AccordionSection label="📊 Day Summary"      open={!!expanded.summary}         onToggle={() => onToggle('summary')}         content={run.day_summary} />
      <AccordionSection label="🌅 Morning Message"  open={!!expanded.morning}         onToggle={() => onToggle('morning')}         content={run.morning_message} />
      {run.blockers_noted && (
        <AccordionSection label="🚧 Blockers"       open={!!expanded.blockers}        onToggle={() => onToggle('blockers')}        content={run.blockers_noted} accent="#c8563a" />
      )}

      {tasks.length > 0 && (
        <TaskListSection label={`📋 Tasks (${tasks.length})`} open={!!expanded.tasks} onToggle={() => onToggle('tasks')} linkTo={taskListUrl}>
          {tasks.map((title, i) => (
            <Link key={i} to={taskListUrl} style={taskRowStyle}>
              <span style={{ fontSize: '11px', color: 'var(--ink-mute)', minWidth: '14px' }}>·</span>
              <span style={{ fontSize: '12.5px', color: 'var(--ink)', flex: 1 }}>{title}</span>
              <span style={{ fontSize: '10px', color: 'var(--ink-mute)' }}>→</span>
            </Link>
          ))}
        </TaskListSection>
      )}

      {completedTasks.length > 0 && (
        <TaskListSection label={`✅ Completed Tasks (${completedTasks.length})`} open={!!expanded.completed_tasks} onToggle={() => onToggle('completed_tasks')} accent="#1a7a5a">
          {completedTasks.map(task => (
            <Link key={task.task_id} to={doneListUrl} style={{ ...taskRowStyle, background: 'rgba(93,202,165,0.06)', borderColor: 'rgba(93,202,165,0.2)' }}>
              <span style={{ fontSize: '12px' }}>✅</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: '12.5px', fontWeight: '500', color: 'var(--ink)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {task.task_title}
                </div>
                <div style={{ display: 'flex', gap: '6px', marginTop: '2px', alignItems: 'center' }}>
                  {task.agent_type && (
                    <span style={{ fontSize: '10px', padding: '1px 6px', borderRadius: '100px', background: 'rgba(55,138,221,0.1)', color: '#1a5fa0', fontWeight: '500' }}>
                      {task.agent_type}
                    </span>
                  )}
                  {task.completed_at && (
                    <span style={{ fontSize: '10px', color: 'var(--ink-mute)' }}>
                      {fmtDate(task.completed_at)} · {timeAgo(task.completed_at)}
                    </span>
                  )}
                </div>
              </div>
              <span style={{ fontSize: '10px', color: 'var(--ink-mute)', flexShrink: 0 }}>→</span>
            </Link>
          ))}
        </TaskListSection>
      )}

      {extraKeys.map(key => (
        <AccordionSection
          key={key}
          label={`📌 ${key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}`}
          open={!!expanded[key]}
          onToggle={() => onToggle(key)}
          content={
            typeof raw[key] === 'string' ? raw[key]
            : Array.isArray(raw[key]) ? raw[key].map(item => `- ${item}`).join('\n')
            : JSON.stringify(raw[key], null, 2)
          }
        />
      ))}
    </div>
  );
}

function AccordionSection({ label, open, onToggle, content, accent }) {
  if (!content) return null;
  return (
    <div style={{ borderBottom: '1px solid var(--paper-2)' }}>
      <button
        onClick={onToggle}
        style={{
          width: '100%', display: 'flex', alignItems: 'center',
          justifyContent: 'space-between', padding: '10px 14px',
          background: 'none', border: 'none', cursor: 'pointer',
          fontFamily: 'var(--sans)', fontSize: '12px', fontWeight: '600',
          color: accent || 'var(--ink-soft)', textAlign: 'left',
        }}
      >
        <span>{label}</span>
        <span style={{
          fontSize: '10px', color: 'var(--ink-mute)',
          transform: open ? 'rotate(180deg)' : 'none',
          transition: 'transform 0.2s', display: 'inline-block',
        }}>▼</span>
      </button>
      {open && (
        <div style={{ padding: '0 14px 12px', animation: 'fadeSlideIn 0.2s ease' }}>
          <div className="run-md" style={{ fontSize: '12.5px', color: 'var(--ink-soft)', lineHeight: 1.7 }}>
            <MdText>{toBullets(content)}</MdText>
          </div>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// RIGHT — Chat Panel
// ═══════════════════════════════════════════════════════════════════════════════

function ChatPanel({ workspaceId }) {
  const [sessions, setSessions]               = useState([]);
  const [sessionId, setSessionId]             = useState(null);
  const [messages, setMessages]               = useState([]);
  const [streamStatus, setStreamStatus]       = useState('');
  const [streaming, setStreaming]             = useState(false);
  const [text, setText]                       = useState('');
  const [creatingSession, setCreatingSession] = useState(false);
  const [summaryStale, setSummaryStale]       = useState(false);
  const [loadingSession, setLoadingSession]   = useState(false);
  const [error, setError]                     = useState(null);

  const messagesEndRef = useRef(null);
  const textareaRef    = useRef(null);

  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages, streaming]);
  useEffect(() => { if (workspaceId) loadSessions(); }, [workspaceId]);

  async function loadSessions() {
    try {
      const data = await apiFetch(`/api/workspaces/${workspaceId}/ceo-chat/sessions?agent=ceo&limit=20`);
      const list = Array.isArray(data) ? data : [];
      setSessions(list);
      if (list.length > 0) await loadSession(list[0].id);
    } catch { setError('Failed to load sessions.'); }
  }

  async function loadSession(id) {
    setLoadingSession(true); setError(null); setStreamStatus('');
    try {
      const data = await apiFetch(`/api/workspaces/${workspaceId}/ceo-chat/sessions/${id}`);
      setSessionId(data.id);
      setMessages(data.messages || []);
      setSummaryStale(data.summary_stale || false);
      setSessions(prev => prev.map(s => s.id === data.id ? { ...s, ...data } : s));
    } catch { setError('Failed to load session.'); }
    finally { setLoadingSession(false); }
  }

  async function createSession() {
    if (!text.trim() || creatingSession) return;
    setCreatingSession(true); setError(null);
    try {
      const data = await apiFetch(`/api/workspaces/${workspaceId}/ceo-chat/sessions`, {
        method: 'POST',
        body: JSON.stringify({ first_message: text.trim(), agent: 'ceo' }),
      });
      setSessions(prev => [data, ...prev]);
      setSessionId(data.id);
      setMessages([]);
      await sendMessage(data.id, text.trim());
    } catch { setError('Failed to create session.'); }
    finally { setCreatingSession(false); }
  }

  async function sendMessage(sid, msg) {
    const targetSession = sid || sessionId;
    const messageText   = msg || text.trim();
    if (!messageText || streaming || !targetSession) return;

    const userMsg = { id: `tmp-${Date.now()}`, role: 'user', content: messageText, created_at: new Date().toISOString() };
    setMessages(prev => [...prev, userMsg]);
    setText('');
    setStreaming(true); setStreamStatus('');

    const assistantId = `stream-${Date.now()}`;
    setMessages(prev => [...prev, { id: assistantId, role: 'assistant', content: '', _streaming: true, created_at: new Date().toISOString() }]);

    try {
      const token = await getToken();
      const url   = `${API_BASE}/api/workspaces/${workspaceId}/ceo-chat/sessions/${targetSession}/messages?token=${encodeURIComponent(token || '')}`;
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: messageText, session_id: targetSession }),
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const reader  = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '', assembled = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() ?? '';
        for (const line of lines) {
          if (line.startsWith(': '))    { setStreamStatus(line.slice(2).trim()); continue; }
          if (line.startsWith('event: ') || line.trim() === '') continue;
          if (line.startsWith('data: ')) {
            const raw = line.slice(6);
            try {
              const parsed = JSON.parse(raw);
              if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
                if (Object.keys(parsed).length === 0) { setStreamStatus(''); continue; }
                if (parsed.detail) { setError(parsed.detail); continue; }
              }
            } catch {}
            const chunk = raw.replace(/\\n/g, '\n');
            assembled += chunk;
            setMessages(prev => prev.map(m => m.id === assistantId ? { ...m, content: assembled } : m));
          }
        }
      }
      setMessages(prev => prev.map(m => m.id === assistantId ? { ...m, _streaming: false } : m));
    } catch (e) {
      setError('Message failed. Please try again.');
      setMessages(prev => prev.filter(m => m.id !== assistantId));
    } finally {
      setStreaming(false); setStreamStatus('');
    }
  }

  function handleKeyDown(e) {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      if (sessionId) sendMessage(); else createSession();
    }
  }

  const canSend = text.trim().length > 0 && !streaming && !creatingSession;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      <div style={{ padding: '20px 20px 14px', borderBottom: '1px solid var(--paper-3)', flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px', flexWrap: 'wrap' }}>
          <div>
            <h2 style={{ fontFamily: 'var(--serif)', fontSize: '18px', fontWeight: '400', margin: 0 }}>Chat with CEO</h2>
            <p style={{ fontSize: '12px', color: 'var(--ink-mute)', margin: '2px 0 0' }}>Strategic conversations · Full visibility</p>
          </div>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
            {sessions.length > 0 && (
              <select
                value={sessionId || ''}
                onChange={e => { if (e.target.value) loadSession(e.target.value); }}
                style={{
                  padding: '5px 10px', border: '1px solid var(--paper-3)',
                  borderRadius: 'var(--r)', background: 'var(--white)',
                  fontSize: '12px', color: 'var(--ink)', cursor: 'pointer',
                  fontFamily: 'var(--sans)', maxWidth: '180px',
                }}
              >
                {sessions.map(s => <option key={s.id} value={s.id}>{(s.title || 'Chat').slice(0, 30)}</option>)}
              </select>
            )}
            <button
              onClick={() => { setSessionId(null); setMessages([]); setSummaryStale(false); setStreamStatus(''); }}
              style={{
                padding: '5px 12px', border: '1px solid var(--paper-3)',
                borderRadius: 'var(--r)', background: 'var(--white)',
                fontSize: '12px', color: 'var(--ink-soft)', cursor: 'pointer',
                fontFamily: 'var(--sans)', whiteSpace: 'nowrap',
              }}
            >
              + New Chat
            </button>
          </div>
        </div>
        {summaryStale && (
          <div style={{
            marginTop: '10px', padding: '6px 10px', borderRadius: 'var(--r)',
            background: 'rgba(232,168,124,0.12)', border: '1px solid rgba(232,168,124,0.3)',
            fontSize: '11px', color: '#8a5a2a', display: 'flex', alignItems: 'center', gap: '6px',
          }}>
            <span style={{ animation: 'pulse 1.5s ease infinite' }}>◉</span>
            Regenerating conversation summary in background…
          </div>
        )}
      </div>

      <div style={{ flex: 1, overflowY: 'auto', padding: '16px 20px' }}>
        {loadingSession
          ? <LoadingRows n={4} />
          : messages.length === 0
            ? <EmptyChat />
            : messages.map(msg => <ChatMessage key={msg.id} msg={msg} />)
        }
        {streamStatus && (
          <div style={{ fontSize: '11px', color: 'var(--ink-mute)', fontStyle: 'italic', padding: '4px 0 8px', textAlign: 'center', animation: 'fadeSlideIn 0.2s ease' }}>
            {streamStatus}
          </div>
        )}
        {error && (
          <div style={{
            margin: '8px 0', padding: '10px 14px',
            background: 'rgba(200,86,58,0.08)', border: '1px solid rgba(200,86,58,0.2)',
            borderRadius: 'var(--r)', fontSize: '12px', color: '#a03020',
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          }}>
            {error}
            <button onClick={() => setError(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', opacity: 0.6 }}>✕</button>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      <div style={{ padding: '12px 20px 20px', borderTop: '1px solid var(--paper-3)', flexShrink: 0, background: 'var(--white)' }}>
        {!sessionId && (
          <p style={{ fontSize: '11px', color: 'var(--ink-mute)', marginBottom: '8px', fontStyle: 'italic' }}>
            Your first message will create a new session.
          </p>
        )}
        <textarea
          ref={textareaRef}
          value={text}
          onChange={e => setText(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={sessionId ? 'Ask about tasks, agents, strategy… (⌘↵ to send)' : 'Start a new CEO conversation… (⌘↵ to send)'}
          rows={3}
          style={{
            width: '100%', boxSizing: 'border-box',
            border: '1px solid var(--paper-3)', borderRadius: 'var(--r)',
            padding: '10px 12px', fontFamily: 'var(--sans)', fontSize: '13px',
            color: 'var(--ink)', background: 'var(--paper)',
            resize: 'none', outline: 'none', lineHeight: 1.55, marginBottom: '10px',
            transition: 'border-color 0.15s',
          }}
          onFocus={e => { e.target.style.borderColor = 'var(--ink-soft)'; }}
          onBlur={e => { e.target.style.borderColor = 'var(--paper-3)'; }}
        />
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px' }}>
          <span style={{ fontSize: '11px', color: 'var(--ink-mute)' }}>CEO Agent · Full visibility</span>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              onClick={() => setText('')}
              disabled={!text.trim()}
              style={{
                padding: '7px 14px', background: 'none',
                border: '1px solid var(--paper-3)', borderRadius: 'var(--r)',
                fontFamily: 'var(--sans)', fontSize: '12px',
                cursor: text.trim() ? 'pointer' : 'default',
                color: 'var(--ink-mute)', opacity: text.trim() ? 1 : 0.4,
              }}
            >
              Clear
            </button>
            <button
              onClick={() => sessionId ? sendMessage() : createSession()}
              disabled={!canSend}
              style={{
                padding: '7px 18px',
                background: canSend ? 'var(--ink)' : 'var(--paper-3)',
                color: canSend ? 'var(--paper)' : 'var(--ink-mute)',
                border: 'none', borderRadius: 'var(--r)',
                fontFamily: 'var(--sans)', fontSize: '13px', fontWeight: '600',
                cursor: canSend ? 'pointer' : 'default',
                display: 'flex', alignItems: 'center', gap: '6px',
                transition: 'background 0.15s',
              }}
            >
              {(streaming || creatingSession)
                ? <><SpinnerInline />{creatingSession ? 'Starting…' : 'Thinking…'}</>
                : (sessionId ? 'Send →' : 'Start Chat →')
              }
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function ChatMessage({ msg }) {
  const isUser = msg.role === 'user';
  return (
    <div style={{
      display: 'flex', flexDirection: isUser ? 'row-reverse' : 'row',
      alignItems: 'flex-start', gap: '10px', marginBottom: '16px',
      animation: 'fadeSlideIn 0.2s ease',
    }}>
      <div style={{
        width: '28px', height: '28px', borderRadius: '50%', flexShrink: 0,
        background: isUser ? 'var(--ink)' : 'rgba(55,138,221,0.15)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: '12px', fontWeight: '600', marginTop: '2px',
        color: isUser ? 'var(--paper)' : '#378add',
      }}>
        {isUser ? 'F' : 'C'}
      </div>
      <div style={{
        maxWidth: '82%',
        background: isUser ? 'var(--ink)' : 'var(--white)',
        color: isUser ? 'var(--paper)' : 'var(--ink)',
        border: isUser ? 'none' : '1px solid var(--paper-3)',
        borderRadius: isUser ? '16px 4px 16px 16px' : '4px 16px 16px 16px',
        padding: '10px 14px', fontSize: '13px', lineHeight: 1.6,
      }}>
        {isUser
          ? <span style={{ whiteSpace: 'pre-wrap' }}>{msg.content}</span>
          : (
            <div className={`chat-md ${msg._streaming ? 'streaming' : ''}`}>
              {msg.content
                ? <MdText>{msg.content}</MdText>
                : <span className="cursor-blink">▍</span>
              }
              {msg._streaming && msg.content && <span className="cursor-blink" style={{ marginLeft: '1px' }}>▍</span>}
            </div>
          )
        }
        <div style={{ fontSize: '10px', marginTop: '6px', opacity: 0.5, textAlign: isUser ? 'left' : 'right' }}>
          {timeAgo(msg.created_at)}
        </div>
      </div>
    </div>
  );
}

const taskRowStyle = {
  display: 'flex', alignItems: 'center', gap: '8px',
  padding: '7px 10px', borderRadius: 'var(--r)',
  border: '1px solid var(--paper-2)',
  textDecoration: 'none', marginBottom: '4px',
  transition: 'background 0.15s', background: 'var(--paper)',
};

function TaskListSection({ label, open, onToggle, accent, children }) {
  return (
    <div style={{ borderBottom: '1px solid var(--paper-2)' }}>
      <button
        onClick={onToggle}
        style={{
          width: '100%', display: 'flex', alignItems: 'center',
          justifyContent: 'space-between', padding: '10px 14px',
          background: 'none', border: 'none', cursor: 'pointer',
          fontFamily: 'var(--sans)', fontSize: '12px', fontWeight: '600',
          color: accent || 'var(--ink-soft)', textAlign: 'left',
        }}
      >
        <span>{label}</span>
        <span style={{ fontSize: '10px', color: 'var(--ink-mute)', transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s', display: 'inline-block' }}>▼</span>
      </button>
      {open && <div style={{ padding: '0 14px 12px', animation: 'fadeSlideIn 0.2s ease' }}>{children}</div>}
    </div>
  );
}

function LoadingRows({ n = 3 }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
      {Array.from({ length: n }).map((_, i) => (
        <div key={i} style={{ height: '80px', borderRadius: 'var(--r)', background: 'var(--paper-2)', animation: 'shimmer 1.4s ease infinite', animationDelay: `${i * 100}ms` }} />
      ))}
    </div>
  );
}

function EmptyState({ icon, text }) {
  return (
    <div style={{ padding: '48px 20px', textAlign: 'center' }}>
      <div style={{ fontSize: '28px', marginBottom: '10px' }}>{icon}</div>
      <p style={{ fontSize: '13px', color: 'var(--ink-mute)' }}>{text}</p>
    </div>
  );
}

function EmptyChat() {
  return (
    <div style={{ padding: '40px 20px', textAlign: 'center' }}>
      <div style={{ fontSize: '32px', marginBottom: '12px' }}>💬</div>
      <p style={{ fontSize: '14px', fontWeight: '500', color: 'var(--ink)', marginBottom: '6px' }}>Chat with your CEO Agent</p>
      <p style={{ fontSize: '12px', color: 'var(--ink-mute)', lineHeight: 1.6, maxWidth: '260px', margin: '0 auto' }}>
        Ask about task progress, agent status, blockers, or start planning the next sprint.
      </p>
    </div>
  );
}

function SpinnerInline() {
  return (
    <span style={{
      display: 'inline-block', width: '11px', height: '11px',
      border: '2px solid rgba(255,255,255,0.3)', borderTopColor: 'rgba(255,255,255,0.9)',
      borderRadius: '50%', animation: 'spin 0.7s linear infinite',
    }} />
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// Page root
// ═══════════════════════════════════════════════════════════════════════════════

export default function CeoRunLogPage() {
  const [searchParams]      = useSearchParams();
  const { activeWorkspace } = useWorkspaces();
  const drawer              = useDrawer();
  const navigate            = useNavigate();
  const [isMobile, setIsMobile] = useState(false);

  // ✅ useHelpBanner called inside the component — correct placement
  const help = useHelpBanner({
    storageKey: 'ceo-run-log-page',
    title: 'CEO Run Log',
    message: 'This page has the daily report from your AI CEO. You can review their work, send them a message and see what\'s going on. Approve their work so that your AI employees can work on next day tasks.',
  });

  useEffect(() => {
    if (!activeWorkspace?.id) return;
    const currentParam = searchParams.get('workspace_id');
    if (currentParam !== activeWorkspace.id) {
      navigate(`/dashboard/ceo-runlog?workspace_id=${activeWorkspace.id}`, { replace: true });
    }
  }, [activeWorkspace?.id]);

  const workspaceId = searchParams.get('workspace_id') || activeWorkspace?.id || '';

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 768px)');
    setIsMobile(mq.matches);
    const handler = e => setIsMobile(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  return (
    <AppShell>
      <style>{`
        @keyframes fadeSlideIn { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes spin    { to { transform: rotate(360deg); } }
        @keyframes shimmer { 0%,100% { opacity:0.5; } 50% { opacity:1; } }
        @keyframes pulse   { 0%,100% { opacity:0.4; } 50% { opacity:1; } }
        @keyframes blink   { 0%,100% { opacity:1;  } 50% { opacity:0; } }
        .cursor-blink { animation: blink 0.9s step-end infinite; }
        .run-md p { margin: 0 0 6px; }
        .run-md ul, .run-md ol { padding-left: 16px; margin: 4px 0 6px; }
        .run-md li { margin-bottom: 3px; }
        .run-md strong { color: var(--ink); }
        .run-md h1,.run-md h2,.run-md h3 { font-size:13px; font-weight:600; margin:8px 0 4px; }
        .chat-md p { margin: 0 0 8px; }
        .chat-md p:last-child { margin-bottom: 0; }
        .chat-md ul, .chat-md ol { padding-left: 18px; margin: 4px 0 8px; }
        .chat-md li { margin-bottom: 3px; }
        .chat-md strong { font-weight: 600; }
        .chat-md code { font-family: monospace; font-size: 12px; background: rgba(0,0,0,0.06); padding: 1px 4px; border-radius: 3px; }
        .chat-md pre { background: rgba(0,0,0,0.04); border-radius: var(--r); padding: 10px 12px; overflow-x: auto; margin: 8px 0; }
        .chat-md pre code { background: none; padding: 0; }
        .chat-md h1,.chat-md h2,.chat-md h3 { font-size: 14px; font-weight: 600; margin: 10px 0 4px; }
        .chat-md blockquote { border-left: 3px solid var(--paper-3); margin: 6px 0; padding: 2px 12px; color: var(--ink-soft); }
        ::-webkit-scrollbar { width: 4px; }
        ::-webkit-scrollbar-track { background: transparent; }
        ::-webkit-scrollbar-thumb { background: var(--paper-3); border-radius: 4px; }
      `}</style>

      {/* ✅ helpButton passed as prop — renders ? in nav after dismiss */}
      <TopNav
        breadcrumb="AI CEO Report"
        drawerOpen={drawer.open}
        onToggleDrawer={drawer.toggle}
        drawerPinned={drawer.pinned}
        helpButton={help.navButton}
      />
      <NavDrawer
        open={drawer.open}
        onClose={drawer.close}
        pinned={drawer.pinned}
        onTogglePin={drawer.togglePin}
      />

      <BodyGrid drawerPinned={drawer.pinned}>
        <PageBody style={{ padding: 0, display: 'flex', flexDirection: 'column', height: 'calc(100vh - 52px)', overflow: 'hidden' }}>
            {help.banner}
          <div style={{ flex: 1, display: 'grid', gridTemplateColumns: '1fr', overflow: 'hidden', minHeight: 0 }}>
            <div style={{
              borderRight: isMobile ? 'none' : '1px solid var(--paper-3)',
              display: 'flex', flexDirection: 'column',
              background: 'var(--paper)', overflow: 'hidden',
            }}>
              <RunLogPanel workspaceId={workspaceId} activeWorkspace={activeWorkspace} />
            </div>
          </div>
        </PageBody>
      </BodyGrid>
    </AppShell>
  );
}